import type { EnglishResourcePageMediaSlug } from './english-resource-page-media';
import { HIDDEN_JOB_MARKET_NETWORKING_202603_URL_PATHS } from '../../../utils/resource-pages/hidden-job-market-networking-202603/hidden-job-market-networking-202603-urls';
import { TECHNOLOGY_SALARY_GUIDE_202604_URL_PATHS } from '../../../utils/resource-pages/technology-salary-guide-202604/technology-salary-guide-202604-urls';
import { TECHNOLOGY_SALARY_GUIDE_URL_PATHS } from '../../../utils/resource-pages/technology-salary-guide/technology-salary-guide-urls';

/** SEO + routing for HTML-embed resource articles available in EN / FI / SV. */
export type TrilingualEmbedLocaleSeo = {
  path: string;
  title: string;
  description: string;
  keywords: string;
  tags: string[];
  /** Localized og:image / twitter:image alt */
  imageAlt: string;
  category: string;
  /** HTML meta article:section */
  articleSection: string;
};

export type TrilingualEmbedArticleSeo = {
  /** Matches {@link RESOURCE_HUB_ARTICLE_CARDS} id where applicable */
  hubCardId:
    | 'finland-job-market-march-2026'
    | 'remote-work-finland'
    | 'salary-transparency'
    | 'salary-transparency-law-2026'
    | 'linkedin-finland'
    | 'cv-finnish-employer'
    | 'cv-template-finland'
    | 'job-market-helsinki-2026'
    | 'job-market-tampere-2026'
    | 'technology-salary-guide-202603'
    | 'technology-salary-guide-202604'
    | 'hidden-job-market-networking-202603';
  /** Folder under pages/resources/ (embed TS outputs) */
  embedSlug: EnglishResourcePageMediaSlug;
  mediaSlug: EnglishResourcePageMediaSlug;
  datePublished: string;
  en: TrilingualEmbedLocaleSeo;
  fi: TrilingualEmbedLocaleSeo;
  sv: TrilingualEmbedLocaleSeo;
};

export const remoteWorkInFinlandTrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'remote-work-finland',
  embedSlug: 'remote-work-in-finland',
  mediaSlug: 'remote-work-in-finland',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/remote-work-in-finland',
    title: 'Remote jobs in Finland 2026 | Where to find them | JobCrawls',
    description:
      'Only 3-7% of listings in Finland’s big cities are remote, but 1,183 location-independent remote jobs are open from Finland right now. Data from 12,039 listings.',
    keywords:
      'remote jobs Finland, remote work Finland, work from home Finland, hybrid jobs Finland, location independent jobs, etätyöpaikat',
    tags: ['Remote jobs Finland', 'Hybrid jobs', 'Finnish job market', 'Work arrangements', 'Job market data'],
    imageAlt: 'Laptop on a desk in a bright workspace, remote and hybrid work in Finland',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/etatyo-suomessa',
    title: 'Etätyöpaikat Suomessa 2026 | Missä etätyötä on | JobCrawls',
    description:
      'Suurten kaupunkien ilmoituksista vain 3-7 % on etätyötä, mutta 1 183 paikkariippumatonta etätyöpaikkaa on haettavissa Suomesta. Data 12 039 ilmoituksesta.',
    keywords:
      'etätyöpaikat, etätyö Suomi, etätyöpaikat Suomi, avoimet etätyöt, hybridityö Suomi, paikkariippumaton työ',
    tags: ['Etätyöpaikat', 'Etätyö', 'Hybridityö', 'Suomen työmarkkinat', 'Työmarkkinadata'],
    imageAlt: 'Tietokone työpöydällä: etä- ja hybridityö Suomessa',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/distansarbete-i-finland',
    title: 'Distansjobb i Finland 2026 | Var de finns | JobCrawls',
    description:
      'Endast 3-7 % av annonserna i de stora städerna är distansjobb, men 1 183 platsoberoende distansjobb kan sökas från Finland. Data från 12 039 annonser.',
    keywords:
      'distansjobb Finland, distansarbete Finland, lediga distansjobb, hybridarbete Finland, platsoberoende jobb',
    tags: ['Distansjobb', 'Distansarbete', 'Hybridarbete', 'Finsk arbetsmarknad', 'Marknadsdata'],
    imageAlt: 'Laptop på skrivbord: distans- och hybridarbete i Finland',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const salaryTransparencyTrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'salary-transparency',
  embedSlug: 'salary-transparency-in-finnish-job-listings',
  mediaSlug: 'salary-transparency-in-finnish-job-listings',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/salary-transparency-in-finnish-job-listings',
    title: 'Salary transparency in Finnish job ads | JobCrawls',
    description:
      'How many Finnish job ads show salary in 2026? Visibility by industry and seniority, trends, and how EU pay transparency will reshape Finnish listings.',
    keywords:
      'salary transparency Finland, Finnish job salary data, pay disclosure Finland, EU pay transparency directive, Finland job listings salary',
    tags: ['Salary transparency', 'Finnish job market', 'Pay disclosure', 'EU directive', 'Job listings'],
    imageAlt: 'Calculator and paperwork on a desk: salary transparency in Finnish job listings',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/palkkalapinakyvyys-suomalaisissa-tyopaikkailmoituksissa',
    title: 'Palkkaläpinäkyvyys työpaikkailmoituksissa | JobCrawls',
    description:
      'Monessako suomalaisessa työpaikkailmoituksessa näkyy palkka 2026? Näkyvyys aloittain ja tasoittain, trendit ja EU-direktiivin tuleva vaikutus ilmoituksiin.',
    keywords:
      'palkkaläpinäkyvyys Suomi, palkkatiedot työpaikkailmoituksissa, EU palkkaläpinäkyvyys, palkka ilmoituksessa',
    tags: ['Palkkaläpinäkyvyys', 'Työmarkkinat', 'Palkkatiedot', 'EU-direktiivi', 'Ilmoitukset'],
    imageAlt: 'Laskin ja paperit: palkkaläpinäkyvyys suomalaisissa työpaikkailmoituksissa',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/lonetransparens-i-finska-jobbannonser',
    title: 'Lönetransparens i finska jobbannonser | JobCrawls',
    description:
      'Ungefär en av fyra finska annonser visar lön. Hur öppna branscher är per fält, erfarenhetsnivå och arbetsform, plus tidslinjen för EU:s lönetransparens.',
    keywords:
      'lönetransparens Finland, lön jobbannons, EU lönetransparens, finländska jobbannonser, disclosed salary',
    tags: ['Lönetransparens', 'Arbetsmarknad', 'Löneuppgifter', 'EU-direktiv', 'Annonser'],
    imageAlt: 'Kalkylator och papper: lönetransparens i finska jobbannonser',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const salaryTransparencyLaw2026TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'salary-transparency-law-2026',
  embedSlug: 'finland-salary-transparency-law-2026',
  mediaSlug: 'finland-salary-transparency-law-2026',
  datePublished: '2026-03-31T00:00:00Z',
  en: {
    path: '/en/resources/finland-salary-transparency-law-2026',
    title: 'Pay transparency Finland | Salary bands guide | JobCrawls',
    description:
      'From 18 May 2026 EU pay transparency rules require earlier salary bands in Finland. What changes for applicants, employers, and pay talks under the directive.',
    keywords:
      'Finland pay transparency law 2026, EU pay transparency directive Finland, salary disclosure Finland, job seeker rights Finland, Finnish salary ranges',
    tags: ['Salary transparency law', 'Finland hiring', 'EU pay transparency', 'Job seekers', 'Pay disclosure'],
    imageAlt: 'Salary transparency law in Finland 2026 with job market data charts',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026',
    title: 'Palkkaläpinäkyvyys 2026 | Palkkavälit hakijalle | JobCrawls',
    description:
      '18.5.2026 alkaen työnantajien on jaettava palkkaväli rekrytoinnissa. Katso nykyinen ilmoitusaste aloittain ja mitä uusi laki tarkoittaa työnhaussasi.',
    keywords:
      'palkkaläpinäkyvyyslaki 2026, palkkatransparenssi Suomi, EU palkkatransparenssidirektiivi, palkkaväli työhaastattelu',
    tags: ['Palkkaläpinäkyvyyslaki', 'Työnhaku Suomessa', 'EU-direktiivi', 'Palkkatiedot', 'Työmarkkinat'],
    imageAlt: 'Palkkaläpinäkyvyyslaki Suomessa 2026 ja työmarkkinadata',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/lonetransparens-lag-finland-2026',
    title: 'Lönetransparens 2026 | Intervall i rekrytering | JobCrawls',
    description:
      'Från 18 maj 2026 måste arbetsgivare dela löneintervall i rekrytering. Se dagens transparens per bransch och vad reglerna betyder i din jobbsökning.',
    keywords:
      'lönetransparenslag Finland 2026, EU lönetransparensdirektiv Finland, löneintervall jobb Finland, löneöppenhet',
    tags: ['Lönetransparenslag', 'Jobbsökning i Finland', 'EU-direktiv', 'Löneuppgifter', 'Arbetsmarknad'],
    imageAlt: 'Lönetransparenslag i Finland 2026 och arbetsmarknadsdata',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const linkedInFinlandTrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'linkedin-finland',
  embedSlug: 'how-to-use-linkedin-to-find-a-job-in-finland',
  mediaSlug: 'how-to-use-linkedin-to-find-a-job-in-finland',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/how-to-use-linkedin-to-find-a-job-in-finland',
    title: 'LinkedIn jobs Finland | Profile and search tips | JobCrawls',
    description:
      'LinkedIn for Finland jobs in 2026: profile keywords recruiters scan, saved searches, messaging tips, and how Finnish hiring differs from typical global norms.',
    keywords:
      'LinkedIn Finland, find job Finland LinkedIn, Finnish job search, LinkedIn profile Finland, networking Finland jobs',
    tags: ['LinkedIn', 'Finland job search', 'Networking', 'Career guide', 'Job seekers'],
    imageAlt: 'LinkedIn on a phone: using LinkedIn to find a job in Finland',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/linkedin-tyonhaku-suomessa',
    title: 'LinkedIn työnhaku Suomessa | Profiili ja haut | JobCrawls',
    description:
      'LinkedIn työnhakuun Suomessa 2026: profiilin hakusanat, tallennetut haut, viestintätavat ja miten suomalainen rekry eroaa kansainvälisistä käytännöistä.',
    keywords:
      'LinkedIn Suomi, työnhaku LinkedIn, suomalainen rekrytointi, LinkedIn-profiili, verkostoituminen työnhaku',
    tags: ['LinkedIn', 'Työnhaku', 'Verkostoituminen', 'Uraneuvot', 'Työnhakijat'],
    imageAlt: 'LinkedIn puhelimessa: työnhaku Suomessa',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/linkedin-jobsok-i-finland',
    title: 'LinkedIn jobb i Finland | Profil och sökning | JobCrawls',
    description:
      'Finska rekryterare använder LinkedIn på sitt sätt. Optimera profilen, sök smart och nätverka utan krångel, och hitta roller som inte syns bara på LinkedIn.',
    keywords:
      'LinkedIn Finland, jobbsökning LinkedIn, finskt rekrytering, LinkedIn-profil, nätverkande jobb',
    tags: ['LinkedIn', 'Jobbsökning', 'Nätverkande', 'Karriärguide', 'Arbetssökande'],
    imageAlt: 'LinkedIn på telefon: jobbsökning i Finland',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const cvFinnishEmployerTrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'cv-finnish-employer',
  embedSlug: 'how-to-write-a-cv-for-a-finnish-employer',
  mediaSlug: 'how-to-write-a-cv-for-a-finnish-employer',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/how-to-write-a-cv-for-a-finnish-employer',
    title: 'CV for Finnish employers | Length and languages | JobCrawls',
    description:
      'Finnish CV norms: tight length, honest language levels, whether to include a photo, and clear relocation signals, plus live hiring data.',
    keywords:
      'CV Finland, Finnish CV, resume Finland, job application Finland, Finnish employer, cover letter Finland',
    tags: ['CV Finland', 'Job application', 'Finnish employer', 'Resume tips', 'Career guide'],
    imageAlt: 'Resume and pen on a desk: writing a CV for a Finnish employer',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/ansioluettelo-suomalaiselle-tyonantajalle',
    title: 'CV suomalaiselle työnantajalle | Rekryvinkit | JobCrawls',
    description:
      'Suomalaiset CV-käytännöt: tiivis pituus, rehelliset kielitasot, kysymys kuvasta ja selkeä sijaintisignaali sekä live-data rekrytoinnista.',
    keywords:
      'CV Suomi, ansioluettelo Suomi, työhakemus Suomi, suomalainen työnantaja, hakemus vinkit',
    tags: ['CV', 'Työhakemus', 'Suomalainen työnantaja', 'Ansioluettelo', 'Ura'],
    imageAlt: 'Ansioluettelo ja kynä: CV suomalaiselle työnantajalle',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/cv-for-finsk-arbetsgivare',
    title: 'CV till finsk arbetsgivare | Språk och signaler | JobCrawls',
    description:
      'Finska CV-normer: tight längd, ärliga språknivåer, frågan om foto och tydliga signaler om flytt, plus data om var rekryteringen koncentreras.',
    keywords:
      'CV Finland, finskt CV, jobbansökan Finland, finsk arbetsgivare, personligt brev',
    tags: ['CV', 'Jobbansökan', 'Finsk arbetsgivare', 'Meritförteckning', 'Karriär'],
    imageAlt: 'CV och penna: skriva CV för finsk arbetsgivare',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const jobMarketHelsinki2026TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'job-market-helsinki-2026',
  embedSlug: 'helsinki-job-market-2026',
  mediaSlug: 'helsinki-job-market-2026',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/helsinki-job-market-2026',
    title: 'Helsinki jobs 2026 | Pay and remote from ads | JobCrawls',
    description:
      'Real salary data, remote work trends, and top hiring fields in Helsinki. Based on 963 active listings in Helsinki, updated March 2026.',
    keywords:
      'Helsinki job market 2026, Helsinki jobs, Helsinki salary data, Helsinki remote work, jobs in Helsinki, Helsinki hiring 2026',
    tags: ['Helsinki', 'Salaries', 'Remote work', 'Hybrid', 'Job market data'],
    imageAlt: 'Helsinki skyline and job market data: salaries, remote work, hiring fields 2026',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/helsingin-tyomarkkinat-2026',
    title: 'Helsingin työpaikat 2026 | Palkat ja etätyö | JobCrawls',
    description:
      'Todellista palkka- ja etätyödataa sekä suurimmat rekrytointialat Helsingissä. 963 aktiivista ilmoitusta (Helsinki kaupunki), päivitetty maaliskuu 2026.',
    keywords:
      'Helsingin työmarkkinat 2026, työpaikat Helsinki, palkat Helsinki, etätyö Helsinki, rekrytointi Helsinki',
    tags: ['Helsinki', 'Palkat', 'Etätyö', 'Hybridityö', 'Työmarkkinadata'],
    imageAlt: 'Helsinki ja työmarkkinadata: palkat, etätyö, rekrytointi 2026',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/helsingfors-arbetsmarknad-2026',
    title: 'Jobb Helsingfors 2026 | Löner och distans | JobCrawls',
    description:
      'Lönedata, distans- och hybridarbete samt största rekryteringsfält i Helsingfors. Baserat på 963 aktiva annonser (stad), uppdaterat mars 2026.',
    keywords:
      'Helsingfors arbetsmarknad 2026, jobb Helsingfors, löner Helsingfors, distansarbete Finland',
    tags: ['Helsingfors', 'Löner', 'Distansarbete', 'Hybrid', 'Marknadsdata'],
    imageAlt: 'Helsingfors och arbetsmarknadsdata: löner, distansarbete, rekrytering 2026',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const jobMarketTampere2026TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'job-market-tampere-2026',
  embedSlug: 'tampere-job-market-2026',
  mediaSlug: 'tampere-job-market-2026',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/tampere-job-market-2026',
    title: 'Tampere jobs 2026 | Pay and top fields from ads | JobCrawls',
    description:
      'Salary benchmarks, work arrangement data, and top hiring fields in Tampere. Based on 643 active listings, updated March 2026.',
    keywords:
      'Tampere job market 2026, Tampere jobs, Tampere salary data, Tampere remote work, jobs in Tampere, Tampere hiring 2026',
    tags: ['Tampere', 'Salaries', 'Technology', 'Job market data', 'Finland'],
    imageAlt: 'Tampere city and job market data: salaries, flexibility, hiring fields 2026',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/tampereen-tyomarkkinat-2026',
    title: 'Tampereen työpaikat 2026 | Palkat ja alat | JobCrawls',
    description:
      'Palkkoja, työjärjestelyjä ja suurimpia rekrytointialoja Tampereella. 643 aktiivista ilmoitusta (Tampere kaupunki), päivitetty maaliskuu 2026.',
    keywords:
      'Tampereen työmarkkinat 2026, työpaikat Tampere, palkat Tampere, teknologia-alan työpaikat Tampere',
    tags: ['Tampere', 'Palkat', 'Teknologia', 'Työmarkkinadata', 'Suomi'],
    imageAlt: 'Tampere ja työmarkkinadata: palkat, joustavuus, rekrytointi 2026',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/tammerfors-arbetsmarknad-2026',
    title: 'Jobb Tammerfors 2026 | Löner och branscher | JobCrawls',
    description:
      'Löner, arbetsformer och största rekryteringsfält i Tammerfors. Baserat på 643 aktiva annonser i Tammerfors, uppdaterat mars 2026.',
    keywords:
      'Tammerfors arbetsmarknad 2026, jobb Tammerfors, löner Tammerfors, teknikjobb Finland',
    tags: ['Tammerfors', 'Löner', 'Teknik', 'Marknadsdata', 'Finland'],
    imageAlt: 'Tammerfors och arbetsmarknadsdata: löner, flexibilitet, rekrytering 2026',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const technologySalaryGuide202603TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'technology-salary-guide-202603',
  embedSlug: 'technology-salary-guide-finland-2026-03',
  mediaSlug: 'technology-salary-guide-finland-2026-03',
  datePublished: '2026-03-18T00:00:00Z',
  en: {
    path: TECHNOLOGY_SALARY_GUIDE_URL_PATHS.en,
    title: 'Finland tech salaries | Medians from job ads | JobCrawls',
    description:
      'March 2026 Finland tech salaries: median monthly pay by seniority with p25–p75 bands, common titles, and how disclosed ranges compare across the market.',
    keywords:
      'finland tech salaries 2026, technology salary guide finland, senior developer salary finland, software engineer salary finland, IT salaries finland',
    tags: ['Tech salaries', 'Finland', 'Compensation', 'Developer pay', 'Market data'],
    imageAlt: 'Technology salary data for roles in Finland: median and ranges',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: TECHNOLOGY_SALARY_GUIDE_URL_PATHS.fi,
    title: 'Teknologia-alan palkat Suomessa 2026 | Mediaanit ilmoituksista | JobCrawls',
    description:
      'Teknologia-alan palkat Suomessa maaliskuu 2026: kuukausimediaanit tasoittain p25–p75-jaoilla, yleiset roolit ja miten ilmoitetut välit vertautuvat markkinaan.',
    keywords:
      'teknologia palkat Suomi 2026, ohjelmistokehittäjä palkka, IT-palkat Suomi, senior kehittäjä palkka',
    tags: ['Teknologia', 'Palkat', 'Suomi', 'Kehittäjät', 'Tilastot'],
    imageAlt: 'Teknologia-alan palkkatiedot Suomessa: mediaanit ja jakaumat',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: TECHNOLOGY_SALARY_GUIDE_URL_PATHS.sv,
    title: 'Tekniklöner Finland | Medianer från annonser | JobCrawls',
    description:
      'Tekniklöner i Finland mars 2026: medianmånadslön per senioritet med p25–p75, vanliga roller och hur utannonserade intervall jämförs på den finska marknaden.',
    keywords:
      'tekniklöner Finland 2026, utvecklare lön Finland, IT-löner Finland, systemutvecklare lön',
    tags: ['Tekniklöner', 'Finland', 'Lönedata', 'Utvecklare', 'Statistik'],
    imageAlt: 'Tekniklöner i Finland: medianer och spridning',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const technologySalaryGuide202604TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'technology-salary-guide-202604',
  embedSlug: 'technology-salary-guide-finland-2026-04',
  mediaSlug: 'technology-salary-guide-finland-2026-04',
  datePublished: '2026-04-02T00:00:00Z',
  en: {
    path: TECHNOLOGY_SALARY_GUIDE_202604_URL_PATHS.en,
    title: 'Finland tech salaries April 2026 | JobCrawls',
    description:
      'Monthly salaries by seniority, top roles from trades to software engineers, and onsite vs hybrid vs remote, based on 1,831 listings.',
    keywords:
      'Finland technology salaries 2026, tech salary Finland April, skilled trades vs software jobs Finland, remote tech jobs Finland, Työmarkkinatori',
    tags: ['Tech salaries', 'Finland', 'Trades', 'Software engineering', 'Work arrangements'],
    imageAlt: 'Technology field salaries in Finland: April 2026 monthly pay by seniority and top roles',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: TECHNOLOGY_SALARY_GUIDE_202604_URL_PATHS.fi,
    title: 'Teknologia-alan palkat Suomessa, huhtikuu 2026 | JobCrawls',
    description:
      'Kuukausipalkat tason mukaan, yleisimmät roolit ammattitöistä ohjelmistokehittäjiin sekä lähityö vs. hybridit vs. etä, 1 831 ilmoituksen pohjalta.',
    keywords:
      'teknologia palkat Suomi 2026, kuukausipalkka IT Suomi, ammattityöt vs. ohjelmisto, etätyö teknologia Suomi',
    tags: ['Palkat', 'Teknologia', 'Suomi', 'Työjärjestelyt', 'Tilastot'],
    imageAlt: 'Teknologia-alan palkat Suomessa: huhtikuu 2026',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: TECHNOLOGY_SALARY_GUIDE_202604_URL_PATHS.sv,
    title: 'Tekniklöner Finland april 2026 | JobCrawls',
    description:
      'Månadslöner per senioritet, vanligaste rollerna från yrkesjobb till utvecklare samt på plats vs hybrid vs distans, baserat på 1 831 annonser.',
    keywords:
      'tekniklöner Finland 2026, månadslön IT Finland, yrkesjobb vs mjukvara, distansarbete teknik Finland',
    tags: ['Tekniklöner', 'Finland', 'Arbetsformer', 'Yrkesroller', 'Data'],
    imageAlt: 'Teknologilöner i Finland: april 2026',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const hiddenJobMarketNetworking202603TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'hidden-job-market-networking-202603',
  embedSlug: 'finland-hidden-job-market-networking-2026-03',
  mediaSlug: 'finland-hidden-job-market-networking-2026-03',
  datePublished: '2026-03-29T00:00:00Z',
  en: {
    path: HIDDEN_JOB_MARKET_NETWORKING_202603_URL_PATHS.en,
    title: 'Finland hidden job market March 2026 | Private-channel share & networking from live data | JobCrawls',
    description:
      '21% of Finnish jobs never reach Työmarkkinatori. Private-channel share by field, salary non-disclosure, and why networking matters.',
    keywords:
      'hidden job market Finland, networking Finland jobs, Työmarkkinatori private listings, Finnish hiring culture, salary non-disclosure Finland',
    tags: ['Hidden job market', 'Networking', 'Finland', 'Recruiting', 'Salary disclosure'],
    imageAlt: 'Professional networking in Finland: hidden job market and private-channel hiring data',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: HIDDEN_JOB_MARKET_NETWORKING_202603_URL_PATHS.fi,
    title: 'Piilotyömarkkinat Suomessa maaliskuu 2026 | Yksityiset kanavat ja verkostoituminen | JobCrawls',
    description:
      '21 % suomalaisista työpaikoista ei koskaan näy Työmarkkinatorilla. Yksityiskanavien osuus aloittain, palkkatietojen piilottaminen ja miksi verkostoituminen ratkaisee.',
    keywords:
      'piilotyömarkkinat Suomi, verkostoituminen työnhaku, Työmarkkinatori, yksityiset rekrykanavat, palkka ilmoituksessa',
    tags: ['Piilotyömarkkinat', 'Verkostoituminen', 'Suomi', 'Rekrytointi', 'Palkkatiedot'],
    imageAlt: 'Ammatillinen verkostoituminen Suomessa: piilotyömarkkinat ja data',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: HIDDEN_JOB_MARKET_NETWORKING_202603_URL_PATHS.sv,
    title: 'Dold arbetsmarknad Finland mars 2026 | Privata kanaler och nätverksdata | JobCrawls',
    description:
      '21 % av finska jobb når aldrig Työmarkkinatori. Andel privata kanaler per bransch, lönehemlighållande och varför nätverkande spelar roll.',
    keywords:
      'dold arbetsmarknad Finland, nätverka jobb Finland, Työmarkkinatori, privata rekryteringskanaler, lönehemlighållande',
    tags: ['Dold arbetsmarknad', 'Nätverkande', 'Finland', 'Rekrytering', 'Lönetransparens'],
    imageAlt: 'Professionellt nätverkande i Finland: dold arbetsmarknad och data',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const finlandJobMarketMarch2026TrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'finland-job-market-march-2026',
  embedSlug: 'finland-job-market-march-2026',
  mediaSlug: 'finland-job-market-march-2026',
  datePublished: '2026-03-27T00:00:00Z',
  en: {
    path: '/en/resources/finland-job-market-march-2026',
    title: 'Finland job market | Sectors and pay from ads | JobCrawls',
    description:
      "Healthcare leads Finland's 16,700+ active listings in March 2026. Full breakdown by field, salary by seniority, and remote work rates.",
    keywords:
      'Finland job market, jobs in Finland 2026, Finnish salaries, remote work Finland, healthcare jobs Finland, tech jobs Finland',
    tags: ['Finland job market', 'Salaries', 'Hiring trends', 'Remote work', 'Market data'],
    imageAlt: 'Team working together: Finland job market overview and hiring data',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/suomen-tyomarkkinat-maaliskuu-2026',
    title: 'Suomen työmarkkinat | Alat, palkat ja etätyö | JobCrawls',
    description:
      'Terveydenhuolto johtaa Suomen yli 16 700 aktiivista ilmoitusta maaliskuussa 2026. Erittely aloittain, palkat kokemustason mukaan ja etätyöosuudet.',
    keywords:
      'Suomen työmarkkinat, työpaikat Suomi 2026, palkat Suomi, etätyö Suomi, rekrytointi tilastot',
    tags: ['Työmarkkinat', 'Palkat', 'Rekrytointi', 'Etätyö', 'Tilastot'],
    imageAlt: 'Tiimi töissä: Suomen työmarkkinat ja rekrytointidata',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/finlands-arbetsmarknad-mars-2026',
    title: 'Finlands arbetsmarknad | Branscher och löner | JobCrawls',
    description:
      'Vården leder Finlands 16 700+ aktiva annonser i mars 2026. Full uppdelning per bransch, medianlön per senioritet och distansarbets­frekvenser.',
    keywords:
      'Finlands arbetsmarknad, jobb Finland 2026, löner Finland, distansarbete, rekrytering statistik',
    tags: ['Arbetsmarknad', 'Löner', 'Rekrytering', 'Distansarbete', 'Data'],
    imageAlt: 'Team som arbetar: Finlands arbetsmarknad och rekryteringsdata',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const cvTemplateFinlandTrilingualSeo: TrilingualEmbedArticleSeo = {
  hubCardId: 'cv-template-finland',
  embedSlug: 'cv-template-finland',
  mediaSlug: 'cv-template-finland',
  datePublished: '2026-08-06T00:00:00Z',
  en: {
    path: '/en/resources/cv-template-finland',
    title: 'Finnish CV template: free download | JobCrawls',
    description:
      'Three free CV templates for the Finnish job market: correct section order, Finnish date format, and IT skills as their own section. Word format.',
    keywords:
      'CV template Finland, Finnish CV template, resume template Finland, free CV template, ansioluettelo template',
    tags: ['CV template', 'Finnish CV', 'Job application', 'Free download', 'Career guide'],
    imageAlt: 'A CV template on a desk, ready to fill in for a Finnish job application',
    category: 'Jobs in Finland',
    articleSection: 'Jobs in Finland',
  },
  fi: {
    path: '/fi/tyoelama/cv-pohja',
    title: 'CV-pohja: lataa ilmainen ansioluettelopohja | JobCrawls',
    description:
      'Kolme ilmaista CV-pohjaa suomalaiseen työnhakuun: oikea osiojärjestys, suomalainen päivämäärämuoto ja IT-taidot omana osionaan. Lataa Word-muodossa.',
    keywords:
      'CV-pohja, ansioluettelopohja, ilmainen CV-pohja, CV malli, ansioluettelo malli',
    tags: ['CV-pohja', 'Ansioluettelo', 'Työnhaku', 'Ilmainen lataus', 'Ura'],
    imageAlt: 'CV-pohja pöydällä valmiina täytettäväksi suomalaiseen työnhakuun',
    category: 'Työt Suomessa',
    articleSection: 'Työelämä',
  },
  sv: {
    path: '/sv/arbetsliv/cv-mall',
    title: 'CV-mall: ladda ner gratis meritförteckning | JobCrawls',
    description:
      'Tre gratis CV-mallar för finsk jobbsökning: rätt sektionsordning, finskt datumformat och IT-kunskaper som egen rubrik. Ladda ner i Word-format.',
    keywords:
      'CV-mall, meritförteckning mall, gratis CV-mall, CV exempel, jobbansökan Finland',
    tags: ['CV-mall', 'Meritförteckning', 'Jobbansökan', 'Gratis nedladdning', 'Karriär'],
    imageAlt: 'En CV-mall på ett skrivbord, redo att fyllas i för en finsk jobbansökan',
    category: 'Jobb i Finland',
    articleSection: 'Karriärresurser',
  },
};

export const TRILINGUAL_EMBED_ARTICLES_SEO: readonly TrilingualEmbedArticleSeo[] = [
  remoteWorkInFinlandTrilingualSeo,
  salaryTransparencyTrilingualSeo,
  salaryTransparencyLaw2026TrilingualSeo,
  linkedInFinlandTrilingualSeo,
  cvFinnishEmployerTrilingualSeo,
  cvTemplateFinlandTrilingualSeo,
  finlandJobMarketMarch2026TrilingualSeo,
  jobMarketHelsinki2026TrilingualSeo,
  jobMarketTampere2026TrilingualSeo,
  technologySalaryGuide202603TrilingualSeo,
  technologySalaryGuide202604TrilingualSeo,
  hiddenJobMarketNetworking202603TrilingualSeo,
];

export const TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID: Record<
  TrilingualEmbedArticleSeo['hubCardId'],
  TrilingualEmbedArticleSeo
> = {
  'remote-work-finland': remoteWorkInFinlandTrilingualSeo,
  'salary-transparency': salaryTransparencyTrilingualSeo,
  'salary-transparency-law-2026': salaryTransparencyLaw2026TrilingualSeo,
  'linkedin-finland': linkedInFinlandTrilingualSeo,
  'cv-finnish-employer': cvFinnishEmployerTrilingualSeo,
  'cv-template-finland': cvTemplateFinlandTrilingualSeo,
  'finland-job-market-march-2026': finlandJobMarketMarch2026TrilingualSeo,
  'job-market-helsinki-2026': jobMarketHelsinki2026TrilingualSeo,
  'job-market-tampere-2026': jobMarketTampere2026TrilingualSeo,
  'technology-salary-guide-202603': technologySalaryGuide202603TrilingualSeo,
  'technology-salary-guide-202604': technologySalaryGuide202604TrilingualSeo,
  'hidden-job-market-networking-202603': hiddenJobMarketNetworking202603TrilingualSeo,
};
